import {
  AfterContentInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  ClearKickbackCount,
  CompleteTask,
  CreateActionItemTaskComment,
  GetKickbackCount,
  MarkActionItemCompleted
} from 'ng2-app/modules/today/state/user-action-items.action';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { Select, Store } from '@ngxs/store';
import { UserActionItem } from '../../models/userActionItem.model';
import { UserActionItemDetailState } from 'ng2-app/modules/today/state/user-action-item-detail.state';
import {
  Observable,
  Subject,
  filter,
  take,
  takeUntil, combineLatest,
} from 'rxjs';
import { notNull } from 'ng2-app/modules/shared/functions/filters';
import {UserActionItemsState, UserActionItemTypes} from 'ng2-app/modules/today/state/user-action-items.state';
import {LenderUser} from 'ng2-app/models/dto/lender-user.model';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.component';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';

@Component({
  selector: 'app-user-action-item-details',
  templateUrl: './user-action-item-details.component.html',
  styleUrls: ['./user-action-item-details.component.scss']
})
export class UserActionItemDetailsComponent implements OnInit, OnDestroy, AfterContentInit {

  showCompletionCheckbox: boolean = true;

  @Select(UserActionItemDetailState.loading) loading$: Observable<boolean>;
  @Select(UserActionItemDetailState.activeActionItem) activeActionItem$: Observable<UserActionItem>;
  @Select(UserActionItemDetailState.commentSaving) commentSaving$: Observable<boolean>;
  @Select(UserActionItemsState.user) actionItemsUser$: Observable<{ fullName: string }>;
  @Select(UserActionItemsState.userActionItemTypes) userActionItemTypes$: Observable<UserActionItemTypes>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(LenderUserState.hasPermissions([
    'canCompleteLenderRequestActionItem',
    'canCompleteKickbackActionItem',
    'canCompleteFundingDeskRequestActionItem',
    'canSeeLenderRequestToggle',
  ])) lenderUserPermissions$: Observable<any>;
  @Select(UserActionItemsState.outstandingKickbacks) outstandingKickbacks$: Observable<number>;

  @ViewChild('commentsList') commentsListRef: ElementRef;
  @ViewChild('detailsViewContainer') detailsViewContainerRef: ElementRef;
  @ViewChild('newCommentContainer') newCommentContainerRef: ElementRef;
  @HostBinding('style.--UAI-details-view-vert-offset') containerVertOffset: string;

  // Action Item Type IDs
  fileReviewTypeId = 6;
  newLeadTypeId = 12;
  makeContactTypeId = 13;
  followUpTypeId = 14;
  newBorrowerNoteTypeId = 16;
  signatureCollectionTypeId = 17;
  offerPendingTypeId = 18;
  offerAcceptedTypeId = 19;

  // Action Items that do not have a Task model
  manuallyCompletableActionTypes = [
    this.newBorrowerNoteTypeId,
    this.signatureCollectionTypeId,
  ];

  // Action Items that do have a Task model, but are not Request types
  manuallyCompletableTaskTypes = [
    this.offerPendingTypeId,
    this.offerAcceptedTypeId,
  ];
  emailActionItemTypes = [
    this.offerPendingTypeId,
  ];
  isManuallyCompletableActionItemType: boolean;
  isManuallyCompletableTaskType: boolean;   // Action Item that has a Task model, and is not a Request type
  callDescription =
    'Make a phone call to this business and disposition the outcome of that call. The task will be cleared after the call is complete.';
  isRequestActionItemType: boolean;
  showEmailButton: boolean;
  formattedDescription?: string;
  newCommentModel = {
    audience: 'internal',
    isForLender: false,
    description: '',
  };

  defaultActionDescriptions = {
    [this.fileReviewTypeId]: 'File Review / Doc Prep',
    [this.newLeadTypeId]: this.callDescription,
    [this.makeContactTypeId]: this.callDescription,
    [this.followUpTypeId]: 'This business has logged into their Borrower Portal with viewable financing options. ' + this.callDescription,
  };

  form = new UntypedFormGroup({});
  options: FormlyFormOptions = {};
  defaultFields: FormlyFieldConfig[] = [
    {
      key: 'description',
      type: 'text',
      props: {
        placeholder: 'Add New Comment',
        autoComplete: 'off',
        keyup: (field, event: KeyboardEvent) => {
          if (event.code === 'Enter') {
            this.addComment();
          }
        }
      },
    },
  ];
  commentAudienceField: FormlyFieldConfig = {
    fieldGroupClassName: 'tw-grid tw-gap-2 tw-grid-cols-[5%_30%] tw-pb-2',
    fieldGroup: [
      {
        className: 'comment-to-label tw-grid tw-grid-flow-col',
        expressions: {
          template: () => {
            return '<div class="tw-pt-4">To:</div>';
          }
        }
      },
      {
        key: 'audience',
        type: 'select',
        props: {
          placeholder: 'Internal/Lender?',
          valueProp: 'id',
          labelProp: 'label',
          defaultValue: 'internal',
          options: [
            {
              id: 'lender',
              label: 'Lender',
            },
            {
              id: 'internal',
              label: 'Internal',
            }
          ]
        },
      },
    ]
  };
  fields: FormlyFieldConfig[] = this.defaultFields;

  private destroyed$ = new Subject<boolean>();

  constructor(
    private dialog: MatDialog,
    private eventBusService: EventBusService,
    private store: Store,
  ) { }

  ngOnInit(): void {

    this.activeActionItem$.pipe(
      takeUntil(this.destroyed$),
      filter(notNull)
    ).subscribe(aai => {
      this.handleKickbackWarning(aai);
    });

    combineLatest([
      this.activeActionItem$,
      this.lenderUserPermissions$,
      this.userActionItemTypes$,
    ]).pipe(
      takeUntil(this.destroyed$),
      filter(notNull),
    ).subscribe(([activeActionItem, lenderUserPermissions, userActionItemTypes]) => {
      this.form.reset();
      const requestTypeIds = userActionItemTypes.requests.map(type => type.id);
      const isLenderRequest = this.isLenderRequestType(activeActionItem);
      const lenderRequestToggle = lenderUserPermissions['canSeeLenderRequestToggle'];
      this.isRequestActionItemType = requestTypeIds.includes(activeActionItem?.typeId);
      this.showEmailButton = this.emailActionItemTypes.includes(activeActionItem?.typeId);
      this.isManuallyCompletableActionItemType = this.isRequestActionItemType
        || this.manuallyCompletableActionTypes.includes(activeActionItem?.typeId);
      this.isManuallyCompletableTaskType = this.manuallyCompletableTaskTypes.includes(activeActionItem?.typeId);
      this.formattedDescription = this.handleFormattedDescription(activeActionItem);
      this.determineCompletionCheckboxVisibility(activeActionItem);
      this.fields = lenderRequestToggle && isLenderRequest
                    ? [
          this.commentAudienceField,
          ...this.defaultFields,
        ]
                    : this.defaultFields;

      if(activeActionItem?.typeId === this.newBorrowerNoteTypeId) {
        this.eventBusService.publish( new this.eventBusService.types.ChangeBorrowerPaneTabEvent('notes') );
      }
    });

  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch(new ClearKickbackCount());
  }

  handleKickbackWarning(activeActionItem: UserActionItem) {
    if (activeActionItem?.type?.toLowerCase() === 'kickback' || activeActionItem?.typeId === 1) {
      this.store.dispatch(new GetKickbackCount(activeActionItem.borrowerId));
    }
  }

  /**
   * If the given ActionItem has a description, format it. Otherwise use the default description.
   */
  handleFormattedDescription(activeActionItem: UserActionItem) {
    if (!activeActionItem) {
      return '';
    }
    return activeActionItem.details?.description
            ? this.stripHtmlTags(activeActionItem.details.description)
            : this.defaultActionDescriptions[activeActionItem.typeId] || '';
  }

  //strips html tags and "KickbackNote: " from task.description because they are saved with html tags in the db
  stripHtmlTags(rawData: string) {
    return rawData?.replace(/<[^>]*>/g, '')
      .replace('KickbackNote: ', '')
      .replace('Contract Request', '');
  }

  isLenderRequestType(activeActionItem) {
    const lenderRequestType = 5,
      lenderRequestCommentType = 8;
    return [lenderRequestType,lenderRequestCommentType].includes(activeActionItem?.typeId);
  }

  addComment(): void {
    this.activeActionItem$
      .pipe(
        take(1)
      ).subscribe(actionItem => {
        const forLender = this.newCommentModel.audience === 'lender';
        this.newCommentModel.isForLender = forLender;
        if(forLender) {
          const dialogRef = this.dialog.open( ConfirmationDialogComponent, {
            data: {
              showHeader: true,
              headerText: 'Are you sure?',
              confirmationPromptText: 'This comment will be sent to the Lender.',
              showCancelButton: true,
            }
          });
          dialogRef.componentInstance.confirm.subscribe( () => {
            dialogRef.close();
            this.finishAddComment( actionItem );
          });
          return;
        }

      this.finishAddComment( actionItem );
    });
  }

  finishAddComment( actionItem ): void {
    this.store.dispatch(new CreateActionItemTaskComment({
      isForLender: this.newCommentModel.isForLender,
      description: this.newCommentModel.description,
    }, actionItem));
    this.commentSaving$.pipe(
      take(2)
    ).subscribe(commentSaving => {
      if (!commentSaving) {
        this.form.reset();
        this.scrollCommentsList();
      }
    });
  }

  ngAfterContentInit(): void {
    this.setContainerOffset();
  }

  scrollCommentsList() {
    this.commentsListRef.nativeElement.scrollTop = this.commentsListRef.nativeElement.scrollHeight;
  }

  setContainerOffset(attempts: number = 0) {
    if (this.detailsViewContainerRef) {
      const rect = this.detailsViewContainerRef.nativeElement.getBoundingClientRect();
      this.containerVertOffset = `${rect.top}px`;
      setTimeout(() => this.scrollCommentsList());
      return;
    }

    if (attempts <= 3) {
      setTimeout(() => this.setContainerOffset(++attempts));
    }

  }

  determineCompletionCheckboxVisibility(actionItem) {
    if (!actionItem) {
      return;
    }

    if (actionItem.type === 'Kickback' || this.isManuallyCompletableActionItemType || this.isManuallyCompletableTaskType) {
      // If any user has a kickback loaded, then they should be able to check completed.
      this.showCompletionCheckbox = true;
    } else if (actionItem.type === 'New File Review' || actionItem.typeId === this.fileReviewTypeId) {
      this.showCompletionCheckbox = false;
    } else {
      this.lenderUserPermissions$.pipe(
        takeUntil(this.destroyed$),
        take(1)
      ).subscribe( lenderUserPermissions => {
        const permName =
          (actionItem.type === 'Lender Request' || actionItem.typeId === 5) ? 'canCompleteLenderRequestActionItem'
          : (actionItem.type === 'Funding Desk Request' || actionItem.typeId === 2) ? 'canCompleteFundingDeskRequestActionItem'
          : '';

        if (permName !== '') {
          // if the user has the correct permission, then display the checkbox.
          this.showCompletionCheckbox = lenderUserPermissions[permName];
        }
      });
    }
  }

  complete() {
    combineLatest([this.lenderUser$, this.activeActionItem$])
    .pipe(
      filter(notNull),
      take(1)
    ).subscribe(([ lenderUser, actionItem ]) => {
      if (!!lenderUser && !!actionItem) {
        if (this.isManuallyCompletableActionItemType) {
          this.store.dispatch( new MarkActionItemCompleted(actionItem.id) );
        }
        if (this.isRequestActionItemType || this.isManuallyCompletableTaskType) {
          this.store.dispatch( new CompleteTask(actionItem.modelId, actionItem.typeId, lenderUser.id) );
        }
      }
    });
  }

}
